import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Input } from './Input';
import { Button } from './Button';
import { authService } from '../services/auth';
import { Colors, Fonts, Radii, Shadows, StatusTints, Typography } from '../constants';

interface ReAuthDialogProps {
  visible: boolean;
  onSuccess: () => void;
  userEmail?: string;
}

export const ReAuthDialog: React.FC<ReAuthDialogProps> = ({
  visible,
  onSuccess,
  userEmail = '',
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(userEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill email when dialog opens
  useEffect(() => {
    if (visible && userEmail) {
      setEmail(userEmail);
    }
  }, [visible, userEmail]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!visible) {
      setPassword('');
      setError(null);
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (!email || !password) {
      setError(t('common.validation.allFieldsRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authService.signIn(email, password);
      onSuccess();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('common.errors.loginFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {}} // Prevent dismissal - must re-auth
    >
      <KeyboardAvoidingView
        style={styles.overlay}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.container}>
          <View style={styles.dialog}>
            <View style={styles.iconCircle}>
              <Ionicons name="lock-closed" size={30} color={StatusTints.primary.fg} />
            </View>

            <Text style={styles.title}>
              {t('auth.reauth.title')}
            </Text>

            <Text style={styles.message}>
              {t('auth.reauth.message')}
            </Text>

            <Input
              label={t('common.labels.email')}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!loading}
            />

            <Input
              label={t('common.labels.password')}
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              editable={!loading}
              onSubmitEditing={handleSubmit}
            />

            {error && (
              <Text style={styles.error}>{error}</Text>
            )}

            <Button
              title={t('auth.reauth.submit')}
              onPress={handleSubmit}
              loading={loading}
              style={styles.submit}
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: Colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '100%',
    maxWidth: 400,
    padding: 20,
  },
  dialog: {
    backgroundColor: Colors.white,
    borderRadius: Radii.dialog,
    padding: 28,
    ...Shadows.panel,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: Radii.card,
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: StatusTints.primary.bg,
    marginBottom: 16,
  },
  title: {
    ...Typography.section,
    color: Colors.textDark,
    marginBottom: 8,
    textAlign: 'center',
  },
  message: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textMedium,
    marginBottom: 20,
    textAlign: 'center',
    lineHeight: 20,
  },
  error: {
    fontFamily: Fonts.regular,
    color: Colors.error,
    fontSize: 13,
    marginBottom: 12,
    textAlign: 'center',
  },
  submit: {
    width: '100%',
  },
});
